import {
  BlockVisibility,
  BlockVisibilityType,
  RecurringOptionType,
  Transparency,
  TransparencyType,
  Visibility,
  VisibilityType,
} from "../../../types/block/enum";
import {
  BlockResponseType,
  BlockType,
  CreateBlockRequestType,
  CreateBlockType,
  UpdateBlockRequestType,
  UpdateBlockType,
  UpdateRecurrenceBlockRequestType,
  UpdateRecurrenceBlockType,
} from "../../../types/block/type";

import moment from "moment";

const SERVER_VISIBILITY_MAP = {
  [BlockVisibility.PUBLIC]: {
    visibility: Visibility.PUBLIC,
    transparency: Transparency.OPAQUE,
  },
  [BlockVisibility.DEEPWORK]: {
    visibility: Visibility.PRIVATE,
    transparency: Transparency.OPAQUE,
  },
  [BlockVisibility.INVISIBLE]: {
    visibility: Visibility.PRIVATE,
    transparency: Transparency.TRANSPARENT,
  },
} as const;

const CLIENT_VISIBILITY_MAP = {
  [`${Visibility.PUBLIC}_${Transparency.OPAQUE}`]: BlockVisibility.PUBLIC,
  [`${Visibility.PUBLIC}_${Transparency.TRANSPARENT}`]: BlockVisibility.PUBLIC,
  [`${Visibility.PRIVATE}_${Transparency.OPAQUE}`]: BlockVisibility.DEEPWORK,
  [`${Visibility.PRIVATE}_${Transparency.TRANSPARENT}`]: BlockVisibility.INVISIBLE,
} as const;

export const toServerVisibility = (clientVisibility: BlockVisibilityType) =>
  SERVER_VISIBILITY_MAP[clientVisibility];

export const toClientVisibility = (visibility: VisibilityType, transparency: TransparencyType) =>
  CLIENT_VISIBILITY_MAP[`${visibility}_${transparency}`];

// allDay는 서버에서 yyyy-mm-dd형식으로 전달되기 떄문에, 클라이언트에서는 00:00:00인 Date객체로 변환
export const formatClientBlockDateTime = ({
  date,
  isAllDay,
}: {
  date: string;
  isAllDay: boolean;
}) => {
  if (isAllDay) {
    const [year, month, day] = date.split("-").map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }
  return new Date(date);
};

const findParentAllDay = (
  parentId: number | undefined,
  serverBlocks: BlockResponseType[] | undefined
) => {
  if (!parentId || !serverBlocks) {
    return false;
  }

  const parent = serverBlocks.find((block) => block.id === parentId);
  if (parent) {
    return parent.detail?.allDay;
  }
  return false;
};

export const convertServerToClientBlockType = ({
  serverBlock,
  defaultVisibility,
  wholeServerBlocks,
}: {
  serverBlock: BlockResponseType;
  defaultVisibility: BlockVisibilityType;
  wholeServerBlocks?: BlockResponseType[];
}): BlockType => {
  const blockVisibility = toServerVisibility(defaultVisibility);

  const defaultDetail = {
    allDay: undefined,
    hangoutLink: undefined,
    meetingCode: undefined,
    linkData: undefined,
    attendees: undefined,
    location: undefined,
    priority: undefined,
  };

  const convertOriginalStart = ({
    originalStart,
    parentAllday,
  }: {
    originalStart: string;
    parentAllday: boolean;
  }) => {
    // ISO 문자열을 Date 객체로 변환
    /**
     * summary
     * 해당 로직은 orginalStart가 있는 경우에만 동작함.
     * originalStart가 있는 경우에는 반복 블록 중 일부 블록인 경우에 한함.
     *
     * 따라서 반복 블록의 경우 -> original 블록, 그러니까 원래 그 위치의 블록이 allday이면 그거에 맞춰서 Date를 00:00:00으로 설정해줌
     * 그 original 블록의 start가 allday이면 00시 00분 00초가 되어야 하고, 아닌 경우에는 원래 시간 그대로 들어가야 함.
     * 따라서 부모 블록의 allday가 true인 경우에 서버에서 보낸 날짜 그대로 추출해서 그 날짜 + 00시 00분 00초로 설정
     */

    // 반복이면 parentIsAllday를 받아서 쓰고, 아닌 경우 본인의 allday를 사용
    const isOriginalPositionBlockAllday = parentAllday;

    if (originalStart.includes("T00:00:00") && isOriginalPositionBlockAllday) {
      // ISO 문자열에서 날짜 부분을 숫자 그대로 추출 // "2025-05-23T"
      const [year, month, day] = originalStart.split("T")[0].split("-").map(Number);

      // 추출한 연도, 월, 일을 사용하여 새로운 Date 객체 생성
      const newAlldayDate = new Date(year, month - 1, day, 0, 0, 0, 0);

      return newAlldayDate;
    }
    return new Date(originalStart);
  };

  const { recurringEventId, ...restOfDetails } = serverBlock.detail ?? {};

  return {
    ...serverBlock,
    ...defaultDetail,
    ...restOfDetails,
    visibility: serverBlock?.visibility ?? blockVisibility?.visibility ?? Visibility.PUBLIC,
    transparency: serverBlock?.transparency ?? blockVisibility?.transparency ?? Transparency.OPAQUE,
    start: serverBlock.start
      ? formatClientBlockDateTime({
          date: serverBlock.start,
          isAllDay: Boolean(serverBlock.detail?.allDay),
        })
      : null,
    end: serverBlock.end
      ? formatClientBlockDateTime({
          date: serverBlock.end,
          isAllDay: Boolean(serverBlock.detail?.allDay),
        })
      : null,
    originalStart: serverBlock.originalStart
      ? convertOriginalStart({
          originalStart: serverBlock.originalStart,
          // NOTE parentAllday가 없는 경우(nullish) allday가 아닌 경우이기 때문에 false로 처리
          parentAllday: findParentAllDay(serverBlock?.originalId, wholeServerBlocks) ?? false,
        })
      : null,
    subBlocks:
      serverBlock.subBlocks?.length > 0
        ? // TODO 현재 subblocks가 없는데, 여기도 data 넣어줘야 할지?
          serverBlock.subBlocks.map((block) =>
            convertServerToClientBlockType({ serverBlock: block, defaultVisibility })
          )
        : [],
  };
};

export const convertClientToServerBlockType = <
  B extends boolean,
  T extends B extends true ? UpdateBlockType | UpdateRecurrenceBlockType : CreateBlockType,
>({
  clientBlock,
  isUpdate,
}: {
  clientBlock: T;
  isUpdate: B;
}): B extends true
  ? T extends { type: RecurringOptionType }
    ? UpdateRecurrenceBlockRequestType
    : UpdateBlockRequestType
  : CreateBlockRequestType => {
  const { allDay, hangoutLink, meetingCode, linkData, attendees, location, priority, ...rest } =
    clientBlock;

  // NOTE undefined인 경우 삭제되도록 처리
  const filteredLinkData = linkData?.map(({ isLoading, ...rest }) => rest);

  const detail = {
    allDay,
    hangoutLink,
    meetingCode,
    linkData: filteredLinkData,
    attendees,
    location,
    priority,
  };

  const filteredDetail = Object.entries(detail)
    .filter(([_, value]) => value !== undefined && value !== null)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  const formatServerBlockDateTime = ({ time, isAllDay }: { time: Date; isAllDay: boolean }) => {
    const date = moment(time);
    if (isAllDay) {
      date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    }

    return date.format("YYYY-MM-DDTHH:mm:ss.SSSZ");
  };

  const baseBlock = {
    ...rest,
    // detailfeild에 아무런 값이 없는 경우 포함되지 않도록 설정
    ...(Object.keys(filteredDetail).length > 0 && { detail: filteredDetail }),
    start: clientBlock.start
      ? formatServerBlockDateTime({ time: clientBlock.start, isAllDay: Boolean(allDay) })
      : clientBlock.start,
    end: clientBlock.end
      ? formatServerBlockDateTime({ time: clientBlock.end, isAllDay: Boolean(allDay) })
      : clientBlock.start,
    originalStart:
      "originalStart" in clientBlock
        ? clientBlock.originalStart
          ? formatServerBlockDateTime({ time: clientBlock.originalStart, isAllDay: false })
          : clientBlock.originalStart
        : undefined,
  };

  if ("zone" in baseBlock) {
    delete baseBlock.zone;
  }

  return baseBlock as unknown as B extends true
    ? T extends { type: RecurringOptionType }
      ? UpdateRecurrenceBlockRequestType
      : UpdateBlockRequestType
    : CreateBlockRequestType;
};
